import { useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { setNavigateHandler } from './services/navigationHandler';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import OrderPage from './pages/order/order.page';
import Drag from './pages/drag/drag.page';
import { AuthContext } from './context/authContext';
import { TenantProvider } from './context/tenantContext';
import 'maplibre-gl/dist/maplibre-gl.css';
import ProtectedRoute from './context/protectedRoute';
import LoginPage from './pages/login/login.page';
import Dashboard from './pages/dashboard/dashboard.page';
import ActiveTrucks from './pages/loadSearch/loadSearch.page';
import ForgotPasswordPage from './pages/login/forgotPassword.page';
import SignUpPage from './pages/login/signUp.page';
import Monitoring from './pages/monitoring/monitoring.page';
import MailReminderPage from './pages/emailReminders/mailReminder.page';
import MailScanOffersPage from './pages/mailScanOffers/mailScanOffers.page';
import { ToastProvider } from './context/toastContext';
import ToastContainer from './components/toast/ToastContainer';
import BookingPage from './pages/bookings/booking.page';
import DriversPage from './pages/drivers/drivers.page';
import ReservationPage from './pages/reservations/reservation.page';
import OfferQueryPage from './pages/offerQueries/OfferQuery.page';
import { Negotiations } from './pages/negotiations/negotiations.page';
import NotFoundPage from './pages/notFound/NotFound.page';
import OAuthAuthorizePage from './pages/oauth/OAuthAuthorize.page';

const AppContent = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  useEffect(() => {
    setNavigateHandler(navigate);
  }, [navigate]);

  if (!authContext) {
    return <div>Loading...</div>;
  }

  const { isLoggedIn, isLoading } = authContext;

  if (isLoading) {
    return <div>Loading...</div>;
  }
  return (
    <TenantProvider>
      <ToastProvider>
        <DndProvider backend={HTML5Backend}>
          <Routes>
            {/* no‐nav pages */}
            <Route path="/login" element={isLoggedIn ? <Navigate to="/" /> : <LoginPage />} />
            <Route path="/forgot-password" element={isLoggedIn ? <Navigate to="/" /> : <ForgotPasswordPage />} />
            <Route path="/sign-up" element={isLoggedIn ? <Navigate to="/" /> : <SignUpPage />} />
            <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />

            <Route
              path="/drag"
              element={
                <ProtectedRoute withLayout={false}>
                  <Drag />
                </ProtectedRoute>
              }
            />
            <Route
              path="/order"
              element={
                <ProtectedRoute withLayout={false}>
                  <OrderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/loadSearch"
              element={
                <ProtectedRoute>
                  <ActiveTrucks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/negotiations"
              element={
                <ProtectedRoute>
                  <Negotiations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring"
              element={
                <ProtectedRoute>
                  <Monitoring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drivers"
              element={
                <ProtectedRoute>
                  <DriversPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking"
              element={
                <ProtectedRoute>
                  <BookingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reservation"
              element={
                <ProtectedRoute>
                  <ReservationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offerQuery"
              element={
                <ProtectedRoute>
                  <OfferQueryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mailReminder"
              element={
                <ProtectedRoute>
                  <MailReminderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mailScanOffers"
              element={
                <ProtectedRoute>
                  <MailScanOffersPage />
                </ProtectedRoute>
              }
            />
            {/* Default route: Load Search (automation-first) */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ActiveTrucks />
                </ProtectedRoute>
              }
            />
            {/* 404 Catch-all route */}
            <Route
              path="*"
              element={
                <ProtectedRoute withLayout={false}>
                  <NotFoundPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          <ToastContainer />
        </DndProvider>
      </ToastProvider>
    </TenantProvider>
  );
};

const App = () => <AppContent />;

export default App;
